import { Check, Sparkles, X } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useSubmitSuggestionFeedback } from "@/lib/api";
import { cn } from "@/lib/utils";

import { PartFigureView } from "./part-figure";
import type { PartSuggestion } from "./types";
import {
  confidencePercent,
  type ConfidenceTier,
  confidenceTier,
  groupSuggestions,
  isLongDescription,
  presentText,
  type SuggestionGroup,
  vendorText,
} from "./utils";

const TIER_BADGE: Record<ConfidenceTier, string> = {
  high: "bg-green-100 text-green-800 border-green-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-red-100 text-red-700 border-red-200",
};

const TIER_LABEL: Record<ConfidenceTier, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

const TIER_BAR: Record<ConfidenceTier, string> = {
  high: "[&>div]:bg-green-500",
  medium: "[&>div]:bg-amber-500",
  low: "[&>div]:bg-red-500",
};

interface FeedbackButtonsProps {
  suggestion: PartSuggestion;
  conversationId: string;
  messageId: string;
}

/**
 * Correct / incorrect toggle for a single suggestion. The button matching the
 * suggestion's current `feedback` (true → check, false → x) renders filled;
 * both are disabled while a submission is in flight so rapid clicks can't race.
 */
function FeedbackButtons({ suggestion, conversationId, messageId }: FeedbackButtonsProps) {
  const submitFeedback = useSubmitSuggestionFeedback();
  const feedback = suggestion.feedback ?? null;

  const submit = (isCorrect: boolean) => {
    if (submitFeedback.isPending) {
      return;
    }
    submitFeedback.mutate({
      conversationId,
      messageId,
      itemId: suggestion.item_id,
      requestedItem: suggestion.requested_item,
      isCorrect,
    });
  };

  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        aria-label="Mark correct"
        aria-pressed={feedback === true}
        title="Mark correct"
        disabled={submitFeedback.isPending}
        onClick={() => submit(true)}
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:opacity-60",
          feedback === true
            ? "border-green-500 bg-green-500 text-white"
            : "border-border text-muted hover:border-green-500 hover:text-green-600",
        )}
      >
        <Check className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label="Mark incorrect"
        aria-pressed={feedback === false}
        title="Mark incorrect"
        disabled={submitFeedback.isPending}
        onClick={() => submit(false)}
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:opacity-60",
          feedback === false
            ? "border-red-500 bg-red-500 text-white"
            : "border-border text-muted hover:border-red-500 hover:text-red-600",
        )}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

interface PartFactProps {
  label: string;
  value: string | null;
  mono?: boolean;
}

/** One labelled fact; an empty one says "Not available" rather than vanishing. */
function PartFact({ label, value, mono = false }: PartFactProps) {
  return (
    <p className="text-muted text-[11px] break-words">
      {label}:{" "}
      {value ? (
        <span className={cn("text-foreground/80 font-medium", mono && "font-mono")}>{value}</span>
      ) : (
        <span className="italic">Not available</span>
      )}
    </p>
  );
}

interface PartFactsProps {
  suggestion: PartSuggestion;
}

/**
 * The spare/wear flag and the vendor — what a technician checks a pick against,
 * the vendor often being the only legible marking left on a worn part. Both
 * always render: catalogue-only parts and many BOM lines carry neither, and a
 * fact that silently isn't there reads as the card leaving it out. Matches the
 * Teams card's `_suggestion_facts`.
 */
function PartFacts({ suggestion }: PartFactsProps) {
  return (
    <div className="mt-1 space-y-0.5">
      <PartFact label="Spare/wear" value={presentText(suggestion.spare_wear_flag)} />
      <PartFact label="Vendor" value={vendorText(suggestion)} />
    </div>
  );
}

interface PartDescriptionProps {
  description: string;
}

/**
 * The BOM line's own description of the part. Content varies wildly — a short
 * one-liner for most rows, a multi-line spec block (dimensions, pressure,
 * temperature, material) for others — so newlines are preserved and anything
 * long is clamped behind a toggle rather than growing the card unbounded.
 */
function PartDescription({ description }: PartDescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = isLongDescription(description);
  const isClamped = collapsible && !expanded;

  return (
    <div className="mt-2">
      <p className="text-muted text-[11px] font-medium tracking-wide uppercase">Description</p>
      <p
        className={cn(
          "text-muted mt-0.5 text-xs leading-5 break-words whitespace-pre-line",
          isClamped && "line-clamp-3",
        )}
      >
        {description}
      </p>
      {collapsible && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="text-primary mt-0.5 text-[11px] font-medium hover:underline"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

interface SuggestionRowProps {
  suggestion: PartSuggestion;
  variant: "primary" | "secondary";
  conversationId: string;
  messageId: string;
}

function SuggestionRow({ suggestion, variant, conversationId, messageId }: SuggestionRowProps) {
  const tier = confidenceTier(suggestion.confidence);
  const isPrimary = variant === "primary";
  const description = presentText(suggestion.description);

  return (
    <div
      className={cn(
        "rounded-lg border",
        isPrimary ? "border-border bg-card p-4" : "border-border/70 bg-background/60 p-3",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "text-primary font-mono font-semibold",
                isPrimary ? "text-base" : "text-sm",
              )}
            >
              {suggestion.item_id}
            </span>
            {suggestion.spare_wear_flag && (
              <Badge variant="secondary" className="text-[10px] tracking-wide uppercase">
                {suggestion.spare_wear_flag}
              </Badge>
            )}
          </div>
          <p
            className={cn("text-foreground mt-0.5 break-words", isPrimary ? "text-sm" : "text-xs")}
          >
            {suggestion.item_name}
          </p>
          <PartFacts suggestion={suggestion} />
        </div>
        <Badge
          variant="outline"
          className={cn("shrink-0 whitespace-nowrap", TIER_BADGE[tier])}
          title={TIER_LABEL[tier]}
        >
          {confidencePercent(suggestion.confidence)}
        </Badge>
      </div>

      <Progress
        value={Math.round(suggestion.confidence * 100)}
        className={cn("mt-2 h-1.5", TIER_BAR[tier])}
      />

      {description && <PartDescription description={description} />}

      {suggestion.reasoning && (
        <p className="text-muted mt-2 text-xs leading-5">{suggestion.reasoning}</p>
      )}

      {suggestion.figure && (
        <PartFigureView figure={suggestion.figure} itemId={suggestion.item_id} />
      )}

      <div className="border-border/60 mt-2.5 flex items-center justify-between gap-2 border-t pt-2">
        <span className="text-muted text-[11px]">Was this the right part?</span>
        <FeedbackButtons
          suggestion={suggestion}
          conversationId={conversationId}
          messageId={messageId}
        />
      </div>
    </div>
  );
}

interface SuggestionGroupBlockProps {
  group: SuggestionGroup;
  conversationId: string;
  messageId: string;
}

/** One requested-part group: a prominent top pick plus any alternatives. */
function SuggestionGroupBlock({ group, conversationId, messageId }: SuggestionGroupBlockProps) {
  const [top, ...alternatives] = group.suggestions;
  if (!top) {
    return null;
  }

  return (
    <div className="space-y-3">
      {group.requestedItem && (
        <p className="text-foreground text-xs font-semibold first-letter:uppercase">
          {group.requestedItem}
        </p>
      )}

      <SuggestionRow
        suggestion={top}
        variant="primary"
        conversationId={conversationId}
        messageId={messageId}
      />

      {alternatives.length > 0 && (
        <div className="space-y-2">
          <p className="text-muted text-[11px] font-medium tracking-wide uppercase">
            Other possible matches
          </p>
          {alternatives.map((suggestion) => (
            <SuggestionRow
              key={suggestion.item_id}
              suggestion={suggestion}
              variant="secondary"
              conversationId={conversationId}
              messageId={messageId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface SuggestionCardProps {
  suggestions: PartSuggestion[];
  conversationId: string;
  messageId: string;
}

/**
 * Ranked Part ID suggestions. A single message may resolve several distinct
 * part requests (e.g. "blades", "impeller", "control cage"); each is rendered
 * as its own group with a top pick plus alternatives. Each suggestion carries a
 * correct/incorrect feedback toggle keyed by `conversationId` + `messageId`.
 */
export function SuggestionCard({ suggestions, conversationId, messageId }: SuggestionCardProps) {
  const groups = groupSuggestions(suggestions).filter((group) => group.suggestions.length > 0);
  if (groups.length === 0) {
    return null;
  }

  const isMultiPart = groups.length > 1;

  return (
    <div className="border-border bg-accent-light/40 mt-3 space-y-3 rounded-xl border p-3">
      <div className="text-primary flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
        <Sparkles className="h-3.5 w-3.5" />
        {isMultiPart ? "Suggested Part IDs" : "Suggested Part ID"}
      </div>

      <div className="space-y-4">
        {groups.map((group) => (
          <SuggestionGroupBlock
            key={group.requestedItem}
            group={group}
            conversationId={conversationId}
            messageId={messageId}
          />
        ))}
      </div>
    </div>
  );
}
